import { useCallback, useRef, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import clsx from "clsx";
import s from "./SplitPane.module.css";

export interface SplitPaneProps {
  direction: "row" | "column";
  /** Size of the first pane in percent. */
  size: number;
  onSizeChange: (size: number) => void;
  min?: number;
  max?: number;
  first: ReactNode;
  second: ReactNode;
  label: string;
}

/** Two panes with a draggable, keyboard-operable divider (WAI-ARIA window splitter). */
export function SplitPane({ direction, size, onSizeChange, min = 25, max = 75, first, second, label }: SplitPaneProps) {
  const root = useRef<HTMLDivElement>(null);
  const clamp = useCallback((v: number) => Math.min(max, Math.max(min, Math.round(v * 10) / 10)), [min, max]);

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    const el = root.current;
    if (!el) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = el.getBoundingClientRect();
    const move = (ev: globalThis.PointerEvent) => {
      const pct = direction === "row" ? ((ev.clientX - rect.left) / rect.width) * 100 : ((ev.clientY - rect.top) / rect.height) * 100;
      onSizeChange(clamp(pct));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.removeProperty("cursor");
    };
    document.body.style.cursor = direction === "row" ? "col-resize" : "row-resize";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const step = e.shiftKey ? 10 : 2;
    const back = direction === "row" ? "ArrowLeft" : "ArrowUp";
    const fwd = direction === "row" ? "ArrowRight" : "ArrowDown";
    if (e.key === back) onSizeChange(clamp(size - step));
    else if (e.key === fwd) onSizeChange(clamp(size + step));
    else if (e.key === "Home") onSizeChange(min);
    else if (e.key === "End") onSizeChange(max);
    else return;
    e.preventDefault();
  }

  return (
    <div ref={root} className={clsx(s.root, s[direction])} style={{ ["--first" as string]: `${size}%` }}>
      <div className={s.first}>{first}</div>
      <div
        role="separator"
        aria-label={label}
        aria-orientation={direction === "row" ? "vertical" : "horizontal"}
        aria-valuenow={Math.round(size)}
        aria-valuemin={min}
        aria-valuemax={max}
        tabIndex={0}
        className={s.divider}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        onDoubleClick={() => onSizeChange(50)}
      />
      <div className={s.second}>{second}</div>
    </div>
  );
}
